import { useState } from 'react';

// Services
import { type BlocFAQType } from '@/services/blocksSchema';

// Components
import Heading from '@/components/atoms/Heading/Heading';
import PreTitle from '@/components/atoms/PreTitle/PreTitle';
import Markdown from '@/components/atoms/Markdown/Markdown';
import Button from '@/components/atoms/Button/Button';

// Hooks
import useResponsive from '@/hooks/useResponsive';

// Utils
import cn from '@/utils/cn';

// Others
import CheckIcon from '@/assets/icons/check.svg?react';

// Style
import styles from './BlocFAQ.module.css';

interface BlocFAQProps {
  items: BlocFAQType['items'];
}

function BlocFAQ({
  items,
}: BlocFAQProps) {
  const { isMobile } = useResponsive();

  const [activeOrder, setActiveOrder] = useState(1);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles['order-list']}>
          {
            items.map(({order, label}) => (
              <button
                key={`faq-order-${order}`}
                type='button'
                onClick={() => setActiveOrder(order)}
                className={cn([
                  styles['order-item'],
                  activeOrder === order ? styles.active : null
                ])}
              >
                <Heading 
                  title={String(order)}
                  size='h2'
                  Tag='span'
                  color='red'
                  weight='700'
                />
                <p className={styles['order-label']}>
                  {label}
                </p>
              </button>
            ))
          }
        </div>
        <div className={styles.items}>
          {
            items.map((item, index) => (
              <div key={`faq-item-${item.order}-${item.title}`} className={cn([
                styles.item,
                activeOrder === item.order || isMobile ? styles.active : null,
                isMobile && index % 2 ? styles['item-bg-grey'] : null
              ])}>
                <div className={styles['item-header']}>
                  <PreTitle
                    preTitle={item.preTitle}
                    size='p1'
                    color='dark'
                  />
                  <Heading
                    title={item.title}
                    size='h2'
                    Tag='h2'
                    color='dark'
                  />
                </div>
                <div className={styles['item-body']}>
                  {
                    item.description ? (
                      <Markdown
                        text={item.description}
                      />
                    ) : null
                  }
                  <div className={styles.checklist}>
                    {
                      item.checklist.map(({ title, description }) => (
                        <div key={`faq-checklist-${title}`}>
                          <div className={styles['checklist-item']}>
                            <CheckIcon />
                            {title}
                          </div>
                          {
                            description ? (
                              <Markdown
                                text={description}
                                className={styles['checklist-item-description']}
                              />
                            ) : null
                          }
                        </div>
                      ))
                    }
                  </div>
                </div>
                <div className={styles.buttons}>
                  {
                    item.primaryButton ? (
                      <Button
                        title={item.primaryButton.title}
                        url={item.primaryButton.url}
                        bgColor='red'
                      />
                    ) : null
                  }
                  {
                    item.secondaryButton ? (
                      <Button
                        title={item.secondaryButton.title}
                        url={item.secondaryButton.url}
                        bgColor='white'
                      />
                    )  : null
                  }
                
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

export default BlocFAQ;
